
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { calculateOriginalAmount, calculateDiscountAmount, calculateFinalAmount } from '@/utils/payment/paymentUtils';
import { PackageType } from '@/types/payment';

interface UseCouponHandlerProps {
  packageType: PackageType;
  userId?: string;
  setDiscount: (discount: number) => void;
  setFinalAmount: (amount: number) => void;
  couponType?: 'Order' | 'Booking';
}

export const useCouponHandler = ({
  packageType,
  userId,
  setDiscount,
  setFinalAmount,
  couponType = 'Order', // Default to Order type coupons
}: UseCouponHandlerProps) => {
  const [couponCode, setCouponCode] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const getOriginalAmount = () => calculateOriginalAmount(packageType || 'silver');

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (!userId) {
      toast.error('Please login to apply coupon');
      return;
    }

    if (!packageType) {
      toast.error('Please select a package first');
      return;
    }

    try {
      setIsApplyingCoupon(true);
      console.log('Applying coupon:', couponCode, 'for user:', userId, 'type:', couponType);
      
      // Check if coupon exists and is valid, now including coupon_type filter
      const { data: couponData, error: couponError } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.trim())
        .eq('active', true)
        .eq('coupon_type', couponType)
        .maybeSingle();

      if (couponError) {
        console.error('Coupon error:', couponError);
        throw new Error('Error validating coupon');
      }

      if (!couponData) {
        throw new Error(`Invalid or expired coupon code for ${couponType.toLowerCase()} type`);
      }

      console.log('Found coupon:', couponData);

      // If this coupon is marked as single-use per user, check if the user has used it before
      if (couponData.single_use_per_user) {
        // Query the appropriate table based on coupon type
        if (couponType === 'Order') {
          const { data: existingOrders, error: existingOrdersError } = await supabase
            .from('orders')
            .select('*')
            .eq('user_id', userId)
            .eq('coupon_code', couponCode)
            .eq('status', 'completed');
          
          if (existingOrdersError) {
            console.error('Error checking coupon usage:', existingOrdersError);
            throw new Error('Error validating coupon');
          }
          
          if (existingOrders && existingOrders.length > 0) {
            throw new Error('You have already used this coupon code');
          }
        } else if (couponType === 'Booking') {
          const { data: existingBookings, error: existingBookingsError } = await supabase
            .from('bookings')
            .select('*')
            .eq('profile_id', userId)
            .eq('coupon_code', couponCode)  // Changed from payment_reference to coupon_code
            .neq('payment_status', 'cancelled');
          
          if (existingBookingsError) {
            console.error('Error checking coupon usage:', existingBookingsError);
            throw new Error('Error validating coupon');
          }
          
          if (existingBookings && existingBookings.length > 0) {
            throw new Error('You have already used this coupon code');
          }
        }
      }

      // Calculate discount
      const originalAmount = getOriginalAmount();
      const discountAmount = calculateDiscountAmount(
        originalAmount,
        couponData.discount_type,
        couponData.discount_value
      );

      // Set final amount
      const finalNewAmount = calculateFinalAmount(originalAmount, discountAmount);
      
      setDiscount(discountAmount);
      setFinalAmount(finalNewAmount);
      
      console.log('Coupon applied successfully:', {
        originalAmount,
        discountAmount,
        finalAmount: finalNewAmount
      });
      
      toast.success('Coupon applied successfully!');
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error(error.message || 'Failed to apply coupon');
      
      // Reset discount in case of error
      setDiscount(0);
      setFinalAmount(getOriginalAmount());
    } finally {
      // Always reset the loading state, regardless of success or failure
      setIsApplyingCoupon(false);
    }
  };

  return {
    couponCode,
    setCouponCode,
    isApplyingCoupon,
    handleApplyCoupon,
    getOriginalAmount,
  };
};
